import React, { useMemo, useState } from 'react';
import { useApp } from '../../../contexts/AppContext';
import { useBudgetData } from '../../../hooks/ProjectFinancials/useBudgetData';
import { useProjectFinancials } from '../../../hooks/ProjectFinancials/useProjectFinancials';
import { useErrorLogging, ERROR_SEVERITY, ERROR_CATEGORY } from '../../../utils/errorLogging';

import PaymentsTab from './PaymentsTab';
import ActiveBudgetTab from './ActiveBudgetTab';
import NeedsQuotesTab from './NeedsQuotesTab';
import PageToolbar from '../../shared/PageToolbar';
import styles from './BudgetPage.module.css';
import pageStyles from '../../../styles/pages.module.css';

// Tab constants
const TABS = {
  ACTIVE_BUDGET: 'active_budget',
  NEEDS_QUOTES: 'needs_quotes',
  PAYMENTS: 'payments',
};

// Tab definitions for PageToolbar
const BUDGET_TABS = [
  { id: TABS.ACTIVE_BUDGET, label: 'תקציב פעיל' },
  { id: TABS.NEEDS_QUOTES, label: 'חסר הצעות' },
  { id: TABS.PAYMENTS, label: 'תשלומים' },
];

const BudgetPage = () => {
  const { currentProject } = useApp();
  const { logError } = useErrorLogging();

  // UI state
  const [activeTab, setActiveTab] = useState(TABS.ACTIVE_BUDGET);

  // Get all budget data
  const {
    data: { projectItems, vendorQuotes },
    referenceData: { costTypes },
    includeMonthlyPayments,
    setIncludeMonthlyPayments,
    includeEstimatedCosts,
    setIncludeEstimatedCosts,
    isLoading,
    error,
    updateProjectInfo,
  } = useBudgetData(currentProject?.id);

  // State for date input error
  const [dateError, setDateError] = useState('');

  // State for editable total budget
  const [budgetInput, setBudgetInput] = useState('');
  const [budgetSaving, setBudgetSaving] = useState(false);

  // Format number as ₪ currency string (whole numbers only, no decimals)
  const formatBudgetCurrency = value => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  // Sync budgetInput when project data loads/changes
  React.useEffect(() => {
    if (currentProject?.total_budget != null) {
      setBudgetInput(formatBudgetCurrency(currentProject.total_budget));
    }
  }, [currentProject?.total_budget]);

  const handleBudgetChange = e => {
    // Store raw value while typing, strip non-digits
    const raw = e.target.value.replace(/[^\d]/g, '');
    setBudgetInput(raw ? `₪ ${Number(raw).toLocaleString()}` : '');
  };

  const handleBudgetBlur = async () => {
    const numericValue = Number(String(budgetInput).replace(/[^\d]/g, ''));
    // Skip if unchanged
    if (numericValue === (currentProject?.total_budget || 0)) return;
    try {
      setBudgetSaving(true);
      await updateProjectInfo({ total_budget: numericValue });
    } catch (err) {
      logError(err, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_MUTATION,
        message: 'Failed to update total budget',
      });
    } finally {
      setBudgetSaving(false);
    }
  };

  // Get financial calculations from hook
  const { budgetUsed, monthlyRecurringTotal, estimatedRecurringTotal, estimatedCostsTotal } =
    useProjectFinancials({
      vendorQuotes: vendorQuotes || [],
      projectItems: projectItems || [],
      costTypes: costTypes || [],
      currentProject,
      includeMonthlyPayments,
      includeEstimatedCosts,
    });

  // Calculate summary statistics
  const summaryData = useMemo(() => {
    return {
      totalBudget: currentProject?.total_budget || 0,
      budgetUsed,
      availableBudget: (currentProject?.total_budget || 0) - budgetUsed,
      monthlyRecurringTotal,
      estimatedRecurringTotal,
    };
  }, [
    currentProject?.total_budget,
    budgetUsed,
    monthlyRecurringTotal,
    estimatedRecurringTotal,
    estimatedCostsTotal,
  ]);

  const handleDateChange = async e => {
    const newDate = e.target.value;
    try {
      setDateError('');
      await updateProjectInfo({
        id: currentProject.id,
        data: {
          estimated_completion: newDate,
        },
      });
      // Date saved successfully
    } catch (error) {
      setDateError('שגיאה בשמירת התאריך');
      logError({
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_MUTATION,
        message: 'Failed to update estimated completion date',
        error,
      });
    }
  };

  // Handle loading and error states
  if (isLoading) {
    return <div className={styles.loadingContainer}>טוען נתונים...</div>;
  }

  if (error) {
    logError(error, {
      severity: ERROR_SEVERITY.ERROR,
      category: ERROR_CATEGORY.DATA_FETCH,
      message: 'Failed to load budget data',
    });
    return <div className={styles.errorContainer}>שגיאה בטעינת נתונים</div>;
  }

  if (!currentProject) {
    return <div className={styles.messageContainer}>אנא בחר פרוייקט</div>;
  }

  return (
    <div className={pageStyles.pageContainer}>
      {/* Summary Section */}
      <div className={pageStyles.summarySection}>
        <div className={pageStyles.summaryCard}>
          <h3 className={pageStyles.summaryCardLabel}>תקציב שהוגדר</h3>
          <input
            type="text"
            inputMode="numeric"
            className={pageStyles.infoInput}
            value={budgetInput}
            onChange={handleBudgetChange}
            onBlur={handleBudgetBlur}
            disabled={budgetSaving}
          />
        </div>
        <div className={pageStyles.summaryCard}>
          <h3 className={pageStyles.summaryCardLabel}>תקציב בשימוש</h3>
          <div className={pageStyles.summaryCardValue}>
            ₪{summaryData.budgetUsed.toLocaleString()}
          </div>
          <div className={styles.checkboxContainer}>
            <div className={styles.checkboxGroup}>
              <div className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  id="includeMonthlyPayments"
                  checked={includeMonthlyPayments}
                  onChange={e => setIncludeMonthlyPayments(e.target.checked)}
                  className={styles.checkbox}
                />
                <label htmlFor="includeMonthlyPayments" className={styles.checkboxLabel}>
                  לכלול תשלומים חודשיים
                </label>
              </div>
              <div className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  id="includeEstimatedCosts"
                  checked={includeEstimatedCosts}
                  onChange={e => setIncludeEstimatedCosts(e.target.checked)}
                  className={styles.checkbox}
                />
                <label
                  htmlFor="includeEstimatedCosts"
                  className={styles.checkboxLabel}
                  title="כולל פריטים ללא הצעת מחיר מאושרת - מחירים משוערים בלבד"
                >
                  לכלול עלויות משוערות
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className={pageStyles.summaryCard}>
          <h3 className={pageStyles.summaryCardLabel}>תקציב פנוי</h3>
          <div className={pageStyles.summaryCardValue}>
            ₪{summaryData.availableBudget.toLocaleString()}
          </div>
        </div>

        {/* New row for recurring payments */}
        <div className={pageStyles.summaryCard}>
          <h3 className={pageStyles.summaryCardLabel}>תאריך סיום פרויקט משוער</h3>
          <div className={styles.dateInputContainer}>
            <input
              type="date"
              value={currentProject?.estimated_completion?.split('T')[0] || ''}
              onChange={handleDateChange}
              className={pageStyles.infoInput}
              dir="ltr"
              placeholder="בחר תאריך"
            />
            {dateError && <div className={styles.error}>{dateError}</div>}
            {!currentProject?.estimated_completion && (
              <p className={pageStyles.summaryCardHint}>
                להצגת עלויות חודשיות, נא להזין תאריך סיום משוער
              </p>
            )}
          </div>
        </div>
        <div className={pageStyles.summaryCard}>
          <h3 className={pageStyles.summaryCardLabel}>תשלומים חודשיים</h3>
          <div className={pageStyles.summaryCardValue}>
            ₪{summaryData.monthlyRecurringTotal?.toLocaleString() || '0'}
          </div>
        </div>
        <div className={pageStyles.summaryCard}>
          <h3 className={pageStyles.summaryCardLabel}>סה&quot;כ תשלומים חודשיים עד סוף הפרויקט</h3>
          <div className={pageStyles.summaryCardValue}>
            ₪{summaryData.estimatedRecurringTotal?.toLocaleString() || '0'}
          </div>
          {!currentProject?.estimated_completion && (
            <p className={pageStyles.summaryCardHint}>נדרש תאריך סיום משוער לחישוב העלות הצפויה</p>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <PageToolbar tabs={BUDGET_TABS} activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Tab Content */}
      {activeTab === TABS.ACTIVE_BUDGET && (
        <div className={pageStyles.card}>
          <ActiveBudgetTab currentProject={currentProject} />
        </div>
      )}

      {activeTab === TABS.NEEDS_QUOTES && (
        <div className={pageStyles.card}>
          <NeedsQuotesTab currentProject={currentProject} />
        </div>
      )}

      {activeTab === TABS.PAYMENTS && (
        <div className={pageStyles.card}>
          <PaymentsTab currentProject={currentProject} />
        </div>
      )}
    </div>
  );
};

export default BudgetPage;
